import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (!token) return setError("El enlace no es válido");
        if (password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
        if (password !== confirm) return setError("Las contraseñas no coinciden");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            setTimeout(() => nav("/login"), 2500);
        } catch (err) {
            setError(formatApiError(err, "No se pudo restablecer la contraseña"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <div className="flex-1 flex items-center justify-center px-6 py-16">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="gf-card">
                        {done ? (
                            <div className="text-center py-4" data-testid="reset-done">
                                <CheckCircle size={48} weight="fill" className="mx-auto mb-4 text-[var(--gf-success)]" />
                                <h2 className="serif text-2xl mb-3">¡Contraseña restablecida!</h2>
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-4">
                                    Te redirigimos al login automáticamente...
                                </p>
                                <Link to="/login" className="btn-primary w-full justify-center">Ir al login</Link>
                            </div>
                        ) : !token ? (
                            <div className="text-center py-4">
                                <Warning size={48} weight="light" className="mx-auto mb-4 text-[var(--gf-warning)]" />
                                <h2 className="serif text-2xl mb-3">Enlace inválido</h2>
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-6">
                                    Este enlace no es válido o ha caducado.
                                </p>
                                <Link to="/recuperar-contrasena" className="btn-gold w-full justify-center">
                                    Solicitar nuevo enlace
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="micro-label mb-2">— Nueva contraseña</div>
                                <h1 className="serif text-3xl mb-2">Restablece tu contraseña</h1>
                                <p className="text-sm text-[var(--gf-text-secondary)] mb-8">
                                    Elige una nueva contraseña de al menos 6 caracteres.
                                </p>
                                <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                                    <div>
                                        <label className="gf-label">Nueva contraseña</label>
                                        <input type="password" required minLength={6} className="gf-input"
                                            value={password} onChange={(e) => setPassword(e.target.value)}
                                            data-testid="reset-password" autoComplete="new-password" />
                                    </div>
                                    <div>
                                        <label className="gf-label">Confirma la contraseña</label>
                                        <input type="password" required minLength={6} className="gf-input"
                                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                            data-testid="reset-confirm" autoComplete="new-password" />
                                    </div>
                                    {error && (
                                        <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="reset-error">
                                            {error}
                                        </div>
                                    )}
                                    <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="reset-submit">
                                        {loading ? "Guardando..." : "Guardar nueva contraseña"} <ArrowRight size={16} />
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </motion.div>
            </div>
            <WhatsAppFab />
        </div>
    );
}
